import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { MIN_BLOCK_MS } from '../data/blockPlan'
import {
  dayGeometry,
  fineDrag,
  freeGaps,
  gapAround,
  hourTicks,
  instantForYSnapped,
  placeInGaps,
  pxPerHourForWindow,
  snapForPxPerHour,
  tapFillsGap,
  yForInstant,
  type DayGeometry,
} from '../lib/timeline'
import { formatClock, formatDayLabel, formatDuration } from '../lib/format'
import { todayKey, type DayKey } from '../lib/time'
import { BlockMenu, type QuickPatch } from './BlockMenu'
import type { Block, Category } from '../data/types'

/**
 * How tall a block has to be before each line of text fits inside it. Text is
 * dropped rather than clipped: half a word is worse than none, and the hover
 * chip carries everything whatever the height.
 */
const MIN_TEXT_PX = 18
const MIN_SECOND_LINE_PX = 35
const MIN_TAGS_PX = 51

const MIN_ZOOM = 0.5
const MAX_ZOOM = 6

function clampZoom(value: number): number {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value))
}

const AXIS_WIDTH = 52
const EDGE_GRAB_PX = 9
/** Below this a column can't carry a legible label, so the week scrolls sideways. */
const MIN_COLUMN_PX = 82

export interface TimelineProps {
  days: DayKey[]
  /** Every block overlapping the shown range, in any order. */
  blocks: Block[]
  categories: Map<string, Category>
  /** Categories still on the grid, for the right-click menu. */
  palette: Category[]
  knownTags: string[]
  dayStartHour: number
  workdayStartHour: number
  workdayEndHour: number
  /** The category a drag on empty time paints with. */
  paintCategoryId: string | null
  showDayHeaders: boolean
  onCreate: (range: { start: number; end: number }) => void
  onResize: (block: Block, bounds: { start?: number; end?: number }) => void
  /** Dragging a seam shared by two blocks moves both at once. */
  onMoveBoundary: (previous: Block, next: Block, at: number) => void
  /** Dragging a block's body moves it in time without resizing it. */
  onMoveBlock: (block: Block, delta: number) => void
  onSelectBlock: (block: Block) => void
  /** Right-click menu: change what a block is without opening the dialog. */
  onQuickPatch: (block: Block, patch: QuickPatch) => void
  onSplitBlock: (block: Block, at: number) => void
  onDeleteBlock: (block: Block) => void
}

type Drag =
  | {
      mode: 'create'
      day: DayKey
      anchor: number
      current: number
      /**
       * The same gesture read to the minute. The step the drag lands on is a
       * convenience, not a floor: when it is coarse enough to swallow a short
       * drag whole, this is what the drag actually asked for.
       */
      fineAnchor: number
      fineCurrent: number
      bounds: { from: number; to: number }
    }
  | { mode: 'resize-start' | 'resize-end'; block: Block; current: number; bounds: { from: number; to: number } }
  | {
      mode: 'boundary'
      previous: Block
      next: Block
      current: number
      bounds: { from: number; to: number }
    }
  | {
      mode: 'move'
      block: Block
      /** How far into the block it was grabbed, so it does not jump under the cursor. */
      grabOffset: number
      length: number
      /** Free stretches the block could land in, computed once at the press. */
      gaps: Array<{ from: number; to: number }>
      /** Where the block currently sits — the placed start, not the pointer. */
      start: number
      current: number
      bounds: { from: number; to: number }
    }

/**
 * The shared timeline behind Today and Week. A day is a column; a week is seven
 * of them side by side. Columns are sized by elapsed hours rather than a fixed
 * 24, so a 23- or 25-hour day is simply shorter or taller than its neighbours.
 */
export function Timeline(props: TimelineProps) {
  const { days, blocks, categories, dayStartHour, workdayStartHour, workdayEndHour } = props
  const scrollRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const [pxPerHour, setPxPerHour] = useState(0)
  const [drag, setDrag] = useState<Drag | null>(null)
  const [now, setNow] = useState(() => Date.now())
  const [zoom, setZoom] = useState(1)
  const [menu, setMenu] = useState<{ block: Block; at: number; x: number; y: number } | null>(null)
  const didScroll = useRef(false)
  /** Viewport-relative y the next zoom should hold still; null means centre. */
  const zoomAnchor = useRef<number | null>(null)

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(id)
  }, [])

  // The working window fills the viewport exactly; the rest of the day scrolls.
  useLayoutEffect(() => {
    const node = scrollRef.current
    if (!node) return
    const measure = () => {
      const available = node.clientHeight - (headerRef.current?.offsetHeight ?? 0)
      setPxPerHour(pxPerHourForWindow(available, workdayStartHour, workdayEndHour))
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [workdayStartHour, workdayEndHour])

  const scaled = pxPerHour * zoom
  const geometries = scaled > 0 ? days.map((day) => dayGeometry(day, dayStartHour, scaled)) : []
  const first = geometries[0]
  const tallest = geometries.reduce((max, geo) => Math.max(max, geo.height), 0)

  // Open on the working day, once — but only after the column has been measured,
  // or the offset would be computed against a height that is about to change.
  useEffect(() => {
    const node = scrollRef.current
    if (!node || !first || didScroll.current || scaled <= 0) return
    const ticks = hourTicks(first)
    const target = ticks.find((tick) => tick.hour === workdayStartHour)
    if (!target) return
    node.scrollTop = target.y
    didScroll.current = true
  }, [first, scaled, workdayStartHour])

  // React registers wheel handlers as passive, so pinch-zoom has to be caught on
  // the node itself or the browser zooms the whole page instead of the timeline.
  useEffect(() => {
    const node = scrollRef.current
    if (!node) return
    const onWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return
      event.preventDefault()
      // Hold the instant under the pointer still; the buttons have no pointer to
      // hold, so they fall back to the middle of the viewport.
      zoomAnchor.current = event.clientY - node.getBoundingClientRect().top
      setZoom((current) => clampZoom(current * (event.deltaY < 0 ? 1.1 : 1 / 1.1)))
    }
    node.addEventListener('wheel', onWheel, { passive: false })
    return () => node.removeEventListener('wheel', onWheel)
  }, [])

  // Whatever sat under the pointer stays under the pointer, rather than the day
  // sliding out from beneath it.
  const lastZoom = useRef(zoom)
  useLayoutEffect(() => {
    const node = scrollRef.current
    if (!node || lastZoom.current === zoom) return
    const ratio = zoom / lastZoom.current
    lastZoom.current = zoom
    const held = zoomAnchor.current ?? node.clientHeight / 2
    zoomAnchor.current = null
    node.scrollTop = (node.scrollTop + held) * ratio - held
  }, [zoom])

  const blocksFor = useCallback(
    (geo: DayGeometry) =>
      blocks
        .filter((block) => block.start < geo.end && (block.end ?? Number.POSITIVE_INFINITY) > geo.start)
        .sort((a, b) => a.start - b.start),
    [blocks],
  )

  function endDrag() {
    if (!drag) return
    if (drag.mode === 'create') {
      const start = Math.min(drag.anchor, drag.current)
      const end = Math.max(drag.anchor, drag.current)
      const fineStart = Math.min(drag.fineAnchor, drag.fineCurrent)
      const fineEnd = Math.max(drag.fineAnchor, drag.fineCurrent)
      if (end - start >= MIN_BLOCK_MS) props.onCreate({ start, end })
      else if (tapFillsGap(drag.bounds, scaled)) {
        props.onCreate({ start: drag.bounds.from, end: drag.bounds.to })
      } else if (fineEnd - fineStart >= MIN_BLOCK_MS) {
        // Zoomed out, a five-minute step can round both ends of a short drag to
        // the same instant and leave nothing at all. A drag that covered a
        // minute meant something, so it gets the minute rather than being lost.
        props.onCreate({ start: fineStart, end: fineEnd })
      }
    } else if (drag.mode === 'boundary') {
      props.onMoveBoundary(drag.previous, drag.next, drag.current)
    } else if (drag.mode === 'move') {
      props.onMoveBlock(drag.block, drag.start - drag.block.start)
    } else if (drag.mode === 'resize-start') {
      props.onResize(drag.block, { start: drag.current })
    } else {
      props.onResize(drag.block, { end: drag.current })
    }
    setDrag(null)
  }


  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <ZoomControl zoom={zoom} step={snapForPxPerHour(scaled)} onChange={setZoom} />
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-auto overscroll-contain">
      <div className="min-w-max">
        {props.showDayHeaders ? (
          <div ref={headerRef} className="sticky top-0 z-20 flex border-b border-line bg-surface">
            <div style={{ width: AXIS_WIDTH }} className="sticky left-0 z-10 shrink-0 bg-surface" />
            {days.map((day) => (
              <DayHeading key={day} day={day} />
            ))}
          </div>
        ) : null}

        <div className="flex" style={{ height: tallest }}>
          <TimeAxis geo={first} />
          {geometries.map((geo) => (
            <DayColumnView
              key={geo.key}
              geo={geo}
              blocks={blocksFor(geo)}
              categories={categories}
              drag={drag}
              now={now}
              paintCategoryId={props.paintCategoryId}
              onDragChange={setDrag}
              onDragEnd={endDrag}
              onSelectBlock={props.onSelectBlock}
              onOpenMenu={setMenu}
            />
          ))}
          </div>
        </div>
      </div>

      {menu ? (
        <BlockMenu
          block={menu.block}
          at={menu.at}
          palette={props.palette}
          knownTags={props.knownTags}
          x={menu.x}
          y={menu.y}
          onPatch={(patch) => props.onQuickPatch(menu.block, patch)}
          onEdit={() => props.onSelectBlock(menu.block)}
          onSplit={(at) => props.onSplitBlock(menu.block, at)}
          onDelete={() => props.onDeleteBlock(menu.block)}
          onClose={() => setMenu(null)}
        />
      ) : null}
    </div>
  )
}

function ZoomControl({
  zoom,
  step,
  onChange,
}: {
  zoom: number
  /** The step a drag currently lands on, in ms. */
  step: number
  onChange: (zoom: number) => void
}) {
  return (
    <div className="absolute top-1 right-2 z-30 flex items-center rounded-md border border-line bg-surface/90 backdrop-blur">
      <ZoomButton label="Zoom out" onClick={() => onChange(clampZoom(zoom / 1.25))} disabled={zoom <= MIN_ZOOM}>
        −
      </ZoomButton>
      <button
        type="button"
        onClick={() => onChange(1)}
        title="Fit the working day. Moving or painting a block lands on this step — zoom in, or hold Alt, for one-minute steps. Dragging an edge always lands on the minute."
        className="px-1 text-[11px] text-faint tnum transition-colors duration-150 hover:text-text"
      >
        {Math.round(zoom * 100)}%
        <span className="pl-1 opacity-70">· {Math.round(step / 60_000)}m</span>
      </button>
      <ZoomButton label="Zoom in" onClick={() => onChange(clampZoom(zoom * 1.25))} disabled={zoom >= MAX_ZOOM}>
        +
      </ZoomButton>
    </div>
  )
}

function ZoomButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string
  onClick: () => void
  disabled: boolean
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="flex size-6 items-center justify-center text-[14px] text-dim transition-colors duration-150 hover:text-text disabled:opacity-25"
    >
      {children}
    </button>
  )
}

function DayHeading({ day }: { day: DayKey }) {
  const isToday = day === todayKey()
  return (
    <div className="flex-1 px-2 py-2 text-center" style={{ minWidth: MIN_COLUMN_PX }}>
      <p
        className={`text-[12px] leading-tight font-medium tracking-wide uppercase ${
          isToday ? 'text-text' : 'text-faint'
        }`}
      >
        {formatDayLabel(day, 'EEE')}
      </p>
      <p className={`text-[17px] leading-tight tnum ${isToday ? 'font-semibold text-text' : 'text-dim'}`}>
        {formatDayLabel(day, 'd')}
      </p>
    </div>
  )
}

function TimeAxis({ geo }: { geo: DayGeometry | undefined }) {
  if (!geo) return null
  return (
    <div
      className="sticky left-0 z-10 shrink-0 select-none bg-surface"
      style={{ width: AXIS_WIDTH, height: geo.height }}
    >
      {hourTicks(geo).map((tick) => (
        <span
          key={tick.instant}
          className="absolute right-2 text-[11px] text-faint tnum"
          style={{ top: tick.y + 3 }}
        >
          {tick.label}
        </span>
      ))}
    </div>
  )
}

interface ColumnProps {
  geo: DayGeometry
  blocks: Block[]
  categories: Map<string, Category>
  drag: Drag | null
  now: number
  paintCategoryId: string | null
  onDragChange: (drag: Drag | null) => void
  onDragEnd: () => void
  onSelectBlock: (block: Block) => void
  onOpenMenu: (menu: { block: Block; at: number; x: number; y: number }) => void
}

function DayColumnView({
  geo,
  blocks,
  categories,
  drag,
  now,
  paintCategoryId,
  onDragChange,
  onDragEnd,
  onSelectBlock,
  onOpenMenu,
}: ColumnProps) {
  const columnRef = useRef<HTMLDivElement>(null)
  // A drag that ends on a block would otherwise also fire its click and open the
  // editor, so a gesture that moved swallows the click that follows it. Cleared
  // when the next gesture starts rather than when a click arrives — a drag that
  // ends over empty space produces no click to clear it, and the flag would
  // otherwise go on to eat the first real click after it.
  const moved = useRef(false)
  /**
   * Where the current gesture pressed, so a wobble is not mistaken for a drag.
   * Kept as a pointer position rather than an instant: the step is not known
   * until the gesture is, and an edge works to a finer one than a body drag.
   */
  const pressedY = useRef(0)

  /**
   * Where on the clock a pointer is, snapped. `fine` asks for one-minute steps
   * whatever the zoom; otherwise the step comes from how tall a minute is on
   * screen, so zooming in is itself the way to get finer. Which of the two a
   * gesture gets is `fineDrag`'s call.
   */
  function instantAt(clientY: number, fine = false): number {
    const rect = columnRef.current?.getBoundingClientRect()
    if (!rect) return geo.start
    return instantForYSnapped(geo, clientY - rect.top, snapForPxPerHour(geo.pxPerHour, fine))
  }

  function beginCreate(event: React.PointerEvent<HTMLDivElement>) {
    if (event.button !== 0 || !paintCategoryId) return
    const at = instantAt(event.clientY, event.altKey)
    const bounds = gapAround(at, blocks, geo)
    if (!bounds) return
    const fine = instantAt(event.clientY, true)
    event.currentTarget.setPointerCapture(event.pointerId)
    onDragChange({
      mode: 'create',
      day: geo.key,
      anchor: at,
      current: at,
      fineAnchor: fine,
      fineCurrent: fine,
      bounds,
    })
  }

  function beginMove(event: React.PointerEvent<HTMLDivElement>, block: Block) {
    event.stopPropagation()
    if (event.button !== 0) return
    const at = instantAt(event.clientY, event.altKey)
    const running = block.end === null
    // A running block has no end to meet anything with, so it stops at the
    // present instead: it cannot be made to have started in the future.
    const length = running ? MIN_BLOCK_MS : (block.end as number) - block.start
    const ceiling = running ? Math.min(now, geo.end) : geo.end
    const gaps = freeGaps(blocks, { start: geo.start, end: ceiling }, block.id)
    event.currentTarget.setPointerCapture(event.pointerId)
    onDragChange({
      mode: 'move',
      block,
      grabOffset: at - block.start,
      length,
      gaps,
      start: block.start,
      current: at,
      bounds: { from: geo.start, to: ceiling },
    })
  }

  function beginResize(event: React.PointerEvent<HTMLElement>, block: Block, edge: 'start' | 'end') {
    event.stopPropagation()
    if (event.button !== 0) return
    event.currentTarget.setPointerCapture(event.pointerId)

    const others = blocks.filter((candidate) => candidate.id !== block.id)
    const blockEnd = block.end ?? geo.end

    // A seam is an edge another block already sits against. Dragging it moves
    // both, rather than refusing because the neighbour is in the way.
    const neighbour =
      edge === 'start'
        ? others.find((candidate) => candidate.end === block.start)
        : others.find((candidate) => candidate.start === blockEnd)

    if (neighbour) {
      const previous = edge === 'start' ? neighbour : block
      const next = edge === 'start' ? block : neighbour
      onDragChange({
        mode: 'boundary',
        previous,
        next,
        current: next.start,
        bounds: { from: previous.start + MIN_BLOCK_MS, to: (next.end ?? geo.end) - MIN_BLOCK_MS },
      })
      return
    }

    const probe = edge === 'start' ? block.start - 1 : blockEnd + 1
    const bounds = gapAround(probe, others, geo) ?? { from: geo.start, to: geo.end }
    onDragChange({
      mode: edge === 'start' ? 'resize-start' : 'resize-end',
      block,
      current: edge === 'start' ? block.start : blockEnd,
      bounds,
    })
  }

  function move(event: React.PointerEvent<HTMLDivElement>) {
    if (!drag) return
    const fine = fineDrag(drag.mode, event.altKey)
    const clamp = (at: number) => Math.min(drag.bounds.to, Math.max(drag.bounds.from, at))
    const clamped = clamp(instantAt(event.clientY, fine))
    // Measured at the step this gesture works in, so a press that lands between
    // two of them is not read as a drag before the pointer has moved.
    if (clamped !== clamp(instantAt(pressedY.current, fine))) moved.current = true
    if (drag.mode === 'move') {
      // Placed where it fits rather than where the pointer is, so meeting a
      // neighbour stops it without pinning it there: carry on past and it lands
      // in the next gap that can hold it.
      const placed = placeInGaps(drag.gaps, clamped, clamped - drag.grabOffset, drag.length)
      onDragChange({ ...drag, current: clamped, start: placed ?? drag.start })
      return
    }
    if (drag.mode === 'create') {
      onDragChange({ ...drag, current: clamped, fineCurrent: clamp(instantAt(event.clientY, true)) })
      return
    }
    onDragChange({ ...drag, current: clamped })
  }

  function previewBounds(block: Block): { start: number; end: number | null } {
    if (drag) {
      if (drag.mode === 'resize-start' && drag.block.id === block.id) {
        return { start: drag.current, end: block.end }
      }
      if (drag.mode === 'resize-end' && drag.block.id === block.id) {
        return { start: block.start, end: drag.current }
      }
      if (drag.mode === 'boundary') {
        if (drag.previous.id === block.id) return { start: block.start, end: drag.current }
        if (drag.next.id === block.id) return { start: drag.current, end: block.end }
      }
      if (drag.mode === 'move' && drag.block.id === block.id) {
        const shift = drag.start - block.start
        return { start: drag.start, end: block.end === null ? null : block.end + shift }
      }
    }
    return { start: block.start, end: block.end }
  }

  const ghost =
    drag && drag.mode === 'create' && drag.day === geo.key
      ? { start: Math.min(drag.anchor, drag.current), end: Math.max(drag.anchor, drag.current) }
      : null
  const paintColor = paintCategoryId ? categories.get(paintCategoryId)?.color : undefined

  return (
    <div
      ref={columnRef}
      onPointerDownCapture={(event) => {
        moved.current = false
        pressedY.current = event.clientY
      }}
      onPointerDown={beginCreate}
      onPointerMove={move}
      onPointerUp={onDragEnd}
      onPointerCancel={onDragEnd}
      className={`relative min-w-0 flex-1 border-l border-line ${
        paintCategoryId ? 'cursor-crosshair' : ''
      }`}
      style={{ height: geo.height, minWidth: MIN_COLUMN_PX, touchAction: 'pan-y' }}
    >
      {hourTicks(geo).map((tick) => (
        <div
          key={tick.instant}
          className="pointer-events-none absolute inset-x-0 border-t border-line/60"
          style={{ top: tick.y }}
        />
      ))}

      <NowLine geo={geo} now={now} />

      {blocks.map((block) => {
        const category = categories.get(block.categoryId)
        const running = block.end === null
        const bounds = previewBounds(block)
        const start = Math.max(bounds.start, geo.start)
        // A running block reaches "now", not the end of the day: it is still
        // being recorded, and drawing it to midnight claims time it has not
        // taken yet.
        const end = Math.min(bounds.end ?? Math.min(now, geo.end), geo.end)
        const top = yForInstant(geo, start)
        const height = Math.max(6, yForInstant(geo, end) - top)
        const label = category?.shortLabel ?? 'unknown'
        const elapsed = (bounds.end ?? now) - bounds.start
        return (
          <div
            key={block.id}
            role="button"
            tabIndex={0}
            onClick={(event) => {
              event.stopPropagation()
              if (moved.current) return
              onSelectBlock(block)
            }}
            onContextMenu={(event) => {
              event.preventDefault()
              event.stopPropagation()
              onOpenMenu({
                block,
                at: instantAt(event.clientY, event.altKey),
                x: event.clientX,
                y: event.clientY,
              })
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') onSelectBlock(block)
            }}
            onPointerDown={(event) => beginMove(event, block)}
            className={`group absolute inset-x-[3px] cursor-grab border-l-[3px] px-1.5 py-0.5 text-left transition-[filter] duration-150 hover:z-20 hover:brightness-110 active:cursor-grabbing ${
              running ? 'rounded-t-md' : 'rounded-md'
            }`}
            style={{
              top,
              height,
              borderLeftColor: category?.color ?? 'var(--tg-line-strong)',
              backgroundColor: category
                ? `color-mix(in oklab, ${category.color} ${running ? 34 : 24}%, var(--tg-surface))`
                : 'var(--tg-raised)',
            }}
          >
            {height >= MIN_TEXT_PX ? (
              <span className="block truncate text-[12px] leading-tight font-medium text-text group-hover:opacity-0">
                {block.note ?? label}
              </span>
            ) : null}
            {height >= MIN_SECOND_LINE_PX ? (
              <span className="block truncate text-[11px] leading-tight text-dim tnum group-hover:opacity-0">
                {block.note ? `${label} · ` : ''}
                {formatClock(new Date(bounds.start))}
                {` · ${formatDuration(elapsed)}`}
                {running ? ' so far' : ''}
              </span>
            ) : null}
            {height >= MIN_TAGS_PX && block.tags.length > 0 ? (
              <span className="block truncate text-[11px] leading-tight text-faint group-hover:opacity-0">
                {block.tags.map((tag) => `#${tag}`).join(' ')}
              </span>
            ) : null}

            {/* The full name and duration, legible even on a ten-minute sliver. */}
            <span className="pointer-events-none absolute top-0 left-0 z-30 hidden max-w-[220px] rounded-md border border-line-strong bg-raised px-1.5 py-1 shadow-lg group-hover:block">
              <span className="block truncate text-[12px] leading-tight font-medium text-text">
                {category?.label ?? block.categoryId}
              </span>
              {block.note ? (
                <span className="block text-[11px] leading-snug text-dim">{block.note}</span>
              ) : null}
              {block.tags.length > 0 ? (
                <span className="block text-[11px] leading-snug text-faint">
                  {block.tags.map((tag) => `#${tag}`).join(' ')}
                </span>
              ) : null}
              <span className="block text-[11px] leading-tight text-dim tnum">
                {formatClock(new Date(bounds.start))}
                {bounds.end ? `–${formatClock(new Date(bounds.end))}` : ''} · {formatDuration(elapsed)}
                {running ? ' so far' : ''}
              </span>
            </span>

            {/* A running block has no bottom edge: it fades out, still going. */}
            {running ? (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 bottom-0 h-4 rounded-b-[2px]"
                style={{
                  background: category
                    ? `linear-gradient(to bottom, transparent, color-mix(in oklab, ${category.color} 34%, var(--tg-surface)))`
                    : undefined,
                  maskImage: 'linear-gradient(to bottom, black, transparent)',
                  WebkitMaskImage: 'linear-gradient(to bottom, black, transparent)',
                }}
              />
            ) : null}

            <span
              onPointerDown={(event) => beginResize(event, block, 'start')}
              className="absolute inset-x-0 top-0 cursor-ns-resize opacity-0 transition-opacity duration-150 group-hover:opacity-100"
              style={{
                height: EDGE_GRAB_PX,
                background: `linear-gradient(to bottom, ${category?.color ?? 'var(--tg-line-strong)'} 0 2px, transparent 2px)`,
              }}
            />
            {!running ? (
              <span
                onPointerDown={(event) => beginResize(event, block, 'end')}
                className="absolute inset-x-0 bottom-0 cursor-ns-resize opacity-0 transition-opacity duration-150 group-hover:opacity-100"
                style={{
                  height: EDGE_GRAB_PX,
                  background: `linear-gradient(to top, ${category?.color ?? 'var(--tg-line-strong)'} 0 2px, transparent 2px)`,
                }}
              />
            ) : null}
          </div>
        )
      })}

      {ghost ? (
        <div
          className="pointer-events-none absolute inset-x-[3px] rounded-md border-l-[3px] px-1.5 py-0.5"
          style={{
            top: yForInstant(geo, ghost.start),
            height: Math.max(4, yForInstant(geo, ghost.end) - yForInstant(geo, ghost.start)),
            borderLeftColor: paintColor ?? 'var(--tg-accent)',
            backgroundColor: paintColor
              ? `color-mix(in oklab, ${paintColor} 32%, var(--tg-surface))`
              : 'var(--tg-raised)',
          }}
        >
          <span className="text-[11px] leading-tight text-text tnum">
            {formatClock(new Date(ghost.start))}–{formatClock(new Date(ghost.end))}
          </span>
        </div>
      ) : null}
    </div>
  )
}

/** A hairline at the present moment, on the day that contains it. */
function NowLine({ geo, now }: { geo: DayGeometry; now: number }) {
  if (now < geo.start || now > geo.end) return null
  return (
    <div
      className="pointer-events-none absolute inset-x-0 z-10 border-t border-danger"
      style={{ top: yForInstant(geo, now) }}
    >
      <span className="absolute -top-[3px] -left-[3px] size-1.5 rounded-full bg-danger" />
    </div>
  )
}
